import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Message } from "./Message";
import Spinner from "./Spinner";

const ParentMemoryChat = ({ chat, formToken, childId, onSaveMemory }) => {
  const [messages, setMessages] = useState(chat.messages || []);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showMemoryForm, setShowMemoryForm] = useState(false);
  const [memoryData, setMemoryData] = useState("");
  const [memoryType, setMemoryType] = useState("anything");
  const messagesEndRef = useRef(null);

  const memoryTypes = [
    { value: "anything", label: "General" },
    { value: "math", label: "Math" },
    { value: "science", label: "Science" },
    { value: "history", label: "History" },
    { value: "geography", label: "Geography" },
    { value: "language_arts", label: "Language Arts" }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = {
      role: "user",
      content: input
    };

    setMessages([...messages, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await axios.post(
        `/api/v1/chats/${chat.id}/send_message`,
        {
          content: input,
          child_id: childId
        },
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const assistantMessage = {
        role: "assistant",
        id: response.data.id,
        content: response.data.content,
        follow_up: response.data.follow_up || [],
      };

      setMessages((prevMessages) => [...prevMessages, assistantMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveMemoryClick = () => {
    setShowMemoryForm(true);
    // Pre-populate with the last assistant message examples if available
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === "assistant");
    if (lastAssistantMessage && lastAssistantMessage.examples && lastAssistantMessage.examples.length > 0) {
      setMemoryData(lastAssistantMessage.examples[0]);
    }
  };

  const handleSaveMemory = async () => {
    if (!memoryData.trim()) return;

    const result = await onSaveMemory(memoryData, memoryType);

    if (result.success) {
      setShowMemoryForm(false);
      setMemoryData("");

      // Add a system message confirming the save
      setMessages([
        ...messages,
        {
          role: "system",
          content: `Memory saved successfully! This information will help personalize the learning experience.`
        }
      ]);
    } else {
      // Handle error
      console.error("Failed to save memory:", result.errors);
    }
  };

  const handleFollowUpClick = (question) => {
    setInput(question);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Creating Learning Memories</h2>
        <p className="text-gray-600">
          Chat with our AI to create personalized learning memories for your child.
          These memories help our tutors provide a more tailored learning experience.
        </p>
      </div>

      <div className="flex-grow overflow-y-auto bg-white rounded-xl shadow-sm p-6 mb-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <Message
              key={`${index}-messages`}
              message={message}
              onFollowUpClick={handleFollowUpClick}
            />
          ))}
          {isLoading && (
            <div className="flex justify-center items-center mt-4">
              <Spinner />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {showMemoryForm && (
        <div className="bg-yellow-50 rounded-xl shadow-sm p-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Save Learning Memory</h3>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Memory Type
            </label>
            <select
              value={memoryType}
              onChange={(e) => setMemoryType(e.target.value)}
              className="w-full rounded-lg border-gray-300 focus:ring-yellow-500 focus:border-yellow-500"
            >
              {memoryTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Memory Content
            </label>
            <textarea
              value={memoryData}
              onChange={(e) => setMemoryData(e.target.value)}
              rows={4}
              className="w-full rounded-lg border-gray-300 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="Enter the memory details..."
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowMemoryForm(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveMemory}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg"
            >
              Save Memory
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex space-x-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-grow rounded-lg border-gray-300 focus:ring-yellow-500 focus:border-yellow-500"
          placeholder="Type your message..."
        />
        <button
          type="submit"
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg"
          disabled={isLoading}
        >
          Send
        </button>
        <button
          type="button"
          onClick={handleSaveMemoryClick}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
        >
          Save Memory
        </button>
      </form>

      <div className="mt-4 text-center">
        <a
          href={`/user_ai_memories?user_id=${childId}`}
          className="text-blue-500 hover:text-blue-700"
        >
          View All Learning Memories
        </a>
      </div>
    </div>
  );
};

export default ParentMemoryChat;